import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useDataProvider, useNotify } from "react-admin";
import type { AgentDispatchRecord } from "../../models/AgentDispatchRecord";
import type { Cargo } from "../../models/Cargo";
import type { AppDataProvider } from "../../types/dataProvider";
import {
  getAgentDispatchStatusColor,
  getAgentDispatchStatusTitle,
} from "../../constants/AgentDispatchStatus";
import { formatDate } from "../../utils/common";
import { AgentCargoInfoView, parseCargoRecord } from "./agent_cargo_info";

const HIDDEN_RAW_KEYS = new Set([
  "id",
  "_id",
  "recordId",
  "record_id",
  "status",
  "state",
  "resultStatus",
  "type",
  "actionType",
  "action_type",
  "operationType",
  "title",
  "name",
  "subject",
  "action",
  "summary",
  "description",
  "desc",
  "remark",
  "content",
  "message",
  "result",
  "output",
  "dayKey",
  "day_key",
  "date",
  "driverId",
  "driver_id",
  "driver",
  "cargoId",
  "cargo_id",
  "cargo",
  "waybillId",
  "waybill_id",
  "waybill",
  "createdAt",
  "created_at",
  "timestamp",
  "time",
  "updatedAt",
  "updated_at",
]);

/**
 * 将未知值格式化为可读文本。
 */
const formatDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * 格式化时间字段。
 */
const formatDateValue = (value: string): string => {
  if (!value) {
    return "-";
  }
  return formatDate(value);
};

/**
 * 详情字段展示。
 */
const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
      {value}
    </Typography>
  </Box>
);

export interface AgentRecordDetailDialogProps {
  open: boolean;
  recordId: string;
  initialRecord?: AgentDispatchRecord | null;
  onClose: () => void;
}

/**
 * 智能体操作记录详情弹窗。
 */
export const AgentRecordDetailDialog: React.FC<AgentRecordDetailDialogProps> = ({
  open,
  recordId,
  initialRecord = null,
  onClose,
}) => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const notify = useNotify();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [record, setRecord] = React.useState<AgentDispatchRecord | null>(null);
  const [cargo, setCargo] = React.useState<Cargo | null>(null);
  const [cargoLoading, setCargoLoading] = React.useState(false);
  const [cargoError, setCargoError] = React.useState("");

  React.useEffect(() => {
    if (!open || !recordId) {
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError("");
        setCargo(null);
        setCargoError("");
        if (initialRecord) {
          setRecord(initialRecord);
        } else {
          setRecord(null);
        }

        const response = await dataProvider.getAgentDispatchRecord(recordId);
        if (cancelled) {
          return;
        }
        setRecord(response.data);
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取操作详情失败";
        if (!initialRecord) {
          setError(message);
        }
        notify(`获取操作详情失败：${message}`, { type: "error" });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, recordId, initialRecord, notify]);

  React.useEffect(() => {
    const cargoId = record?.cargoId;
    if (!open || !cargoId) {
      setCargo(null);
      setCargoError("");
      setCargoLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCargo = async () => {
      try {
        setCargoLoading(true);
        setCargoError("");
        const response = await dataProvider.getOne("validCargos", { id: cargoId });
        if (cancelled) {
          return;
        }
        const nextCargo = parseCargoRecord(response.data);
        setCargo(nextCargo);
        if (!nextCargo) {
          setCargoError("未获取到货源信息");
        }
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取货源信息失败";
        setCargo(null);
        setCargoError(message);
      } finally {
        if (!cancelled) {
          setCargoLoading(false);
        }
      }
    };

    void fetchCargo();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, record?.cargoId]);

  const extraFields = React.useMemo(() => {
    if (!record?.raw) {
      return [];
    }
    return Object.entries(record.raw)
      .filter(([key, value]) => !HIDDEN_RAW_KEYS.has(key) && value !== undefined && value !== null && value !== "")
      .map(([key, value]) => ({ key, value: formatDisplayValue(value) }));
  }, [record]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>智能体操作详情</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" alignItems="center" gap={1.5} py={2}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              正在加载操作详情...
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!error && record && (
          <Stack spacing={2}>
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.default",
                px: 2,
                py: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ wordBreak: "break-all" }}>
                    {record.title || record.type || "未命名操作"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                    记录 ID：{record.id || recordId || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {record.type && <Chip label={record.type} size="small" variant="outlined" />}
                  {record.status && (
                    <Chip
                      label={getAgentDispatchStatusTitle(record.status)}
                      size="small"
                      color={getAgentDispatchStatusColor(record.status)}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 2,
              }}
            >
              <DetailField label="日期" value={record.dayKey || "-"} />
              <DetailField label="创建时间" value={formatDateValue(record.createdAt)} />
              <DetailField label="更新时间" value={formatDateValue(record.updatedAt)} />
              <DetailField label="司机 ID" value={record.driverId || "-"} />
              <DetailField label="货源 ID" value={record.cargoId || "-"} />
              <DetailField label="运单 ID" value={record.waybillId || "-"} />
            </Box>

            {record.cargoId && (
              <>
                <Divider />
                <Typography variant="subtitle2">货源信息</Typography>
                {cargoError && (
                  <Alert severity="warning">
                    {cargoError}
                  </Alert>
                )}
                <AgentCargoInfoView
                  cargo={cargo}
                  loading={cargoLoading}
                  emptyText="暂无货源详情"
                />
              </>
            )}

            {(record.summary || record.content) && (
              <>
                <Divider />
                {record.summary && <DetailField label="摘要" value={record.summary} />}
                {record.content && <DetailField label="内容" value={record.content} />}
              </>
            )}

            {extraFields.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2">其他字段</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                  }}
                >
                  {extraFields.map((field) => (
                    <DetailField key={field.key} label={field.key} value={field.value} />
                  ))}
                </Box>
              </>
            )}
          </Stack>
        )}
        {!loading && !error && !record && (
          <Typography variant="body2" color="text.secondary">
            暂无详情数据
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentRecordDetailDialog;
